import React, { useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { MTableToolbar } from '@material-table/core';

import FormTextField from 'components/FormFields/FormTextField';

import compose from 'helpers/compose';
import { isoDateTimeToLabel } from 'helpers/helpers';
import MaterialTable from 'components/Table/Table';
import { RootStore } from 'stores/rootStore';
import { ADMIN_ACCOUNT_EVENTS } from 'api/endpoints';

import useStyles from 'pages/Accounts/AccountsList/styles/Accounts';

interface AccountHistory {
  event: string;
  time: string;
  user: string;
  ipAddress: string;
  detail?: string;
}

interface Props {
  formikProps: any;
  rootStore: RootStore;
}

const AccountHistoryTable: React.FC<Props> = ({ formikProps, rootStore }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [data, setData] = useState<AccountHistory[] | null>(null);

  const [filtersVisible, setFilterVisible] = useState(false);

  const toggleFilterVisibility = () => {
    setFilterVisible(!filtersVisible);
  };

  const fetchData = async (accountId: string) => {
    rootStore.stores.uiStore.setLoading(true);
    const response = await rootStore.stores.apiStore.get<AccountHistory[]>(ADMIN_ACCOUNT_EVENTS(accountId));
    if (response?.data) setData(response?.data);
    rootStore.stores.uiStore.setLoading(false);
  };

  return (
    <>
      <Grid item xs={12} container alignItems="center">
        <Grid item xs={5}>
          <FormTextField name="accountId" label={t(`general_accountId`)} margin="dense" />
        </Grid>
        <Grid item xs={2}>
          <Button variant="contained" color="primary" onClick={() => fetchData(formikProps.values.accountId)}>
            {t('general_confirm')}
          </Button>
        </Grid>
      </Grid>
      {data && (
        <Grid item xs={12} alignItems="center">
          <MaterialTable
            components={{
              Toolbar: (props) => (
                <div className={classes.toolbar}>
                  <div className={classes.originalToolbar}>
                    <MTableToolbar {...props} />
                  </div>
                  <div className={classes.buttons}>
                    <Button variant="contained" onClick={toggleFilterVisibility}>
                      {t('general_show_filters')}
                    </Button>
                  </div>
                </div>
              ),
            }}
            options={{
              header: true,
              sorting: true,
              draggable: false,
              search: true,
              toolbar: true,
              paging: true,
              pageSize: 20,
              pageSizeOptions: [10, 20, 50, 100],
              searchFieldAlignment: 'left',
              headerStyle: {
                borderBottom: '2px solid rgb(224, 224, 224)',
              },
              filtering: filtersVisible,
              emptyRowsWhenPaging: false
            }}
            data={data}
            columns={[
              {
                title: t('general_date'),
                field: 'time',
                defaultSort: 'desc',
                render: ({ time }) => isoDateTimeToLabel(time),
                cellStyle: { width: '5%' }
              },
              {
                title: t('backOffice_account_history_event'),
                field: 'event',
                render: ({ event }) => t(`account_event_${event}`),
              },
              {
                title: t('general_username'),
                field: 'user',
              },
              {
                title: t('general_detail'),
                field: 'detail',
              },
            ]}
          />
        </Grid>
      )}
    </>
  );
};

export default compose(inject('rootStore'), observer)(AccountHistoryTable);
